import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * The in-app confirm (shadcn AlertDialog — never browser confirm(), D-C).
 * Every row-level delete and every irreversible action asks through this and
 * then runs the same mutation its detail dialog runs. Lifted out of
 * components/medical 2026-09-10 so Tasks, Calendar and Finances share it.
 */
export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  destructive?: boolean;
  /**
   * The dismiss button's label, for an action whose own verb is "cancel"
   * (#2413: "Cancel task" beside "Cancel", "Cancelar tarea" beside
   * "Cancelar"). Name it by its outcome ("Keep task"). Omitted, it reads the
   * shared common actions.cancel.
   */
  cancelLabel?: string;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = true,
  cancelLabel,
}: ConfirmActionDialogProps) {
  // #2109 R3 fix pass: callers pass a translated title and confirm label; the
  // Cancel beside them reads the shared action key so the pair agrees.
  const { t } = useTranslation('common');
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {/* A title can carry the family's own text (a task title), so it wraps. */}
          <AlertDialogTitle className="break-words">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel ?? t('actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
